import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const ConversionFunnelAnalysis = ({ dateRange, refreshTrigger }) => {
  const [funnelData, setFunnelData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFunnelData();

    // Set up real-time subscription
    const channel = supabase?.channel('conversion_funnel')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        () => {
          loadFunnelData();
        }
      )?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_subscriptions'
        },
        () => {
          loadFunnelData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [dateRange, refreshTrigger]);

  const getDateFilter = () => {
    const now = new Date();
    let startDate;

    switch (dateRange) {
      case '24h':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return null;
    }

    return startDate?.toISOString();
  };

  const loadFunnelData = async () => {
    setLoading(true);
    try {
      let startDate = getDateFilter();

      // Get total user profiles (registrations)
      let profileQuery = supabase?.from('user_profiles')?.select('id, created_at', { count: 'exact' });

      if (startDate) {
        profileQuery = profileQuery?.gte('created_at', startDate);
      }

      const { data: profiles, count: totalUsers, error: profileError } = await profileQuery;

      if (profileError) {
        if (isSchemaError(profileError)) {
          console.error('Schema error:', profileError?.message);
          throw profileError;
        } else {
          console.log('Error loading profiles:', profileError?.message);
          setFunnelData([]);
          return;
        }
      }

      // Get agent usage metrics for onboarding and activation
      let metricsQuery = supabase?.from('agent_usage_metrics')?.select('user_id, agent_id, metric_type, created_at');

      if (startDate) {
        metricsQuery = metricsQuery?.gte('created_at', startDate);
      }

      const { data: metrics, error: metricsError } = await metricsQuery;

      if (metricsError) {
        if (isSchemaError(metricsError)) {
          console.error('Schema error:', metricsError?.message);
          throw metricsError;
        }
      }

      // Get subscriptions with tier from subscription_plans
      let subscriptionQuery = supabase?.from('user_subscriptions')?.select(`
        user_id,
        created_at,
        cancel_at_period_end,
        subscription_plans (
          tier
        )
      `);

      if (startDate) {
        subscriptionQuery = subscriptionQuery?.gte('created_at', startDate);
      }

      const { data: subscriptions, error: subscriptionError } = await subscriptionQuery;

      if (subscriptionError) {
        if (isSchemaError(subscriptionError)) {
          console.error('Schema error:', subscriptionError?.message);
          throw subscriptionError;
        }
      }

      // Process funnel data
      processFunnelData(totalUsers || 0, profiles || [], metrics || [], subscriptions || []);
    } catch (error) {
      console.error('Error loading funnel data:', error);
      setFunnelData([]);
    } finally {
      setLoading(false);
    }
  };

  const isSchemaError = (error) => {
    if (!error) return false;
    if (error?.code && typeof error?.code === 'string') {
      const errorClass = error?.code?.substring(0, 2);
      if (errorClass === '42' || errorClass === '08') return true;
    }
    return false;
  };

  const processFunnelData = (totalUsers, profiles, metrics, subscriptions) => {
    // Calculate funnel steps
    const registeredUsers = totalUsers;
    
    // Users who started onboarding (have any agent interaction)
    const usersWithMetrics = new Set(metrics?.map(m => m?.user_id));
    const onboardingStarted = usersWithMetrics?.size;

    // Users who completed onboarding (have onboarding agent interaction)
    const onboardingCompleted = new Set(
      metrics?.filter(m => m?.agent_id === 'onboarding')?.map(m => m?.user_id)
    )?.size;

    // Users who activated first agent (any agent interaction)
    const firstAgentActivated = new Set(
      metrics?.filter(m => m?.metric_type === 'interaction')?.map(m => m?.user_id)
    )?.size;

    // Users who sent first message
    const firstMessageSent = new Set(
      metrics?.filter(m => m?.metric_type === 'interaction' || m?.metric_type === 'api_call')?.map(m => m?.user_id)
    )?.size;

    // Users with active subscriptions (not cancelled)
    const activeSubscriptions = new Set(
      subscriptions?.filter(s => !s?.cancel_at_period_end)?.map(s => s?.user_id)
    )?.size;

    // Users who upgraded (non-free tier)
    const upgradedUsers = new Set(
      subscriptions?.filter(s => s?.subscription_plans?.tier !== 'free' && !s?.cancel_at_period_end)?.map(s => s?.user_id)
    )?.size;

    // Build funnel with actual numbers
    const baseUsers = Math.max(registeredUsers, 100); // Ensure we have a baseline
    
    const funnel = [
      {
        step: 'Landing Page Visit',
        users: baseUsers,
        conversionRate: 100,
        dropOffRate: 0,
        icon: 'Home'
      },
      {
        step: 'Registration Completed',
        users: registeredUsers,
        conversionRate: baseUsers > 0 ? ((registeredUsers / baseUsers) * 100)?.toFixed(1) : 0,
        dropOffRate: baseUsers > 0 ? (((baseUsers - registeredUsers) / baseUsers) * 100)?.toFixed(1) : 0,
        icon: 'CheckCircle'
      },
      {
        step: 'Onboarding Started',
        users: onboardingStarted,
        conversionRate: baseUsers > 0 ? ((onboardingStarted / baseUsers) * 100)?.toFixed(1) : 0,
        dropOffRate: registeredUsers > 0 ? (((registeredUsers - onboardingStarted) / registeredUsers) * 100)?.toFixed(1) : 0,
        icon: 'Rocket'
      },
      {
        step: 'Onboarding Completed',
        users: onboardingCompleted,
        conversionRate: baseUsers > 0 ? ((onboardingCompleted / baseUsers) * 100)?.toFixed(1) : 0,
        dropOffRate: onboardingStarted > 0 ? (((onboardingStarted - onboardingCompleted) / onboardingStarted) * 100)?.toFixed(1) : 0,
        icon: 'Award'
      },
      {
        step: 'First Agent Activated',
        users: firstAgentActivated,
        conversionRate: baseUsers > 0 ? ((firstAgentActivated / baseUsers) * 100)?.toFixed(1) : 0,
        dropOffRate: onboardingCompleted > 0 ? (((onboardingCompleted - firstAgentActivated) / onboardingCompleted) * 100)?.toFixed(1) : 0,
        icon: 'Zap'
      },
      {
        step: 'First Message Sent',
        users: firstMessageSent,
        conversionRate: baseUsers > 0 ? ((firstMessageSent / baseUsers) * 100)?.toFixed(1) : 0,
        dropOffRate: firstAgentActivated > 0 ? (((firstAgentActivated - firstMessageSent) / firstAgentActivated) * 100)?.toFixed(1) : 0,
        icon: 'MessageSquare'
      },
      {
        step: 'Active Subscription',
        users: activeSubscriptions,
        conversionRate: baseUsers > 0 ? ((activeSubscriptions / baseUsers) * 100)?.toFixed(1) : 0,
        dropOffRate: firstMessageSent > 0 ? (((firstMessageSent - activeSubscriptions) / firstMessageSent) * 100)?.toFixed(1) : 0,
        icon: 'CreditCard'
      },
      {
        step: 'Subscription Upgrade',
        users: upgradedUsers,
        conversionRate: baseUsers > 0 ? ((upgradedUsers / baseUsers) * 100)?.toFixed(1) : 0,
        dropOffRate: activeSubscriptions > 0 ? (((activeSubscriptions - upgradedUsers) / activeSubscriptions) * 100)?.toFixed(1) : 0,
        icon: 'TrendingUp'
      }
    ];

    setFunnelData(funnel);
  };

  const getOverallConversionRate = () => {
    if (funnelData?.length === 0) return 0;
    const firstStep = funnelData?.[0]?.users || 1;
    const lastStep = funnelData?.[funnelData?.length - 1]?.users || 0;
    return ((lastStep / firstStep) * 100)?.toFixed(1);
  };

  const getTotalDropOff = () => {
    if (funnelData?.length === 0) return 0;
    const firstStep = funnelData?.[0]?.users || 1;
    const lastStep = funnelData?.[funnelData?.length - 1]?.users || 0;
    return firstStep - lastStep;
  };

  const getHighestDropOffStep = () => {
    if (funnelData?.length === 0) return 'N/A';
    const maxDropOff = Math.max(...funnelData?.map(item => parseFloat(item?.dropOffRate || 0)));
    const step = funnelData?.find(item => parseFloat(item?.dropOffRate || 0) === maxDropOff);
    return step?.step || 'N/A';
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-warm p-6">
        <p className="text-muted-foreground text-center">Loading funnel data...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm p-6 space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Target" size={20} color="var(--color-green-500)" />
            <span className="text-sm text-muted-foreground font-caption">Overall Conversion</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getOverallConversionRate()}%</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={20} color="var(--color-orange-500)" />
            <span className="text-sm text-muted-foreground font-caption">Total Drop-off</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getTotalDropOff()}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertCircle" size={20} color="var(--color-red-500)" />
            <span className="text-sm text-muted-foreground font-caption">Highest Drop-off</span>
          </div>
          <p className="text-sm font-semibold text-foreground font-heading leading-tight">{getHighestDropOffStep()}</p>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground font-heading">Conversion Funnel Breakdown</h3>
        
        {funnelData?.length === 0 ? (
          <div className="bg-muted/30 rounded-lg p-8 text-center">
            <p className="text-muted-foreground">No conversion data available for the selected date range</p>
          </div>
        ) : (
          <div className="space-y-3">
            {funnelData?.map((step, index) => {
              const widthPercentage = parseFloat(step?.conversionRate) || 0;
              const isFirstStep = index === 0;
              const isLastStep = index === funnelData?.length - 1;
              
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name={step?.icon} size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground font-heading">
                          {step?.step}
                        </h4>
                        <p className="text-xs text-muted-foreground font-caption">
                          {step?.users?.toLocaleString()} users
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-foreground font-heading">
                        {step?.conversionRate}%
                      </p>
                      {!isFirstStep && parseFloat(step?.dropOffRate) > 0 && (
                        <p className="text-xs text-red-500 font-caption">
                          -{step?.dropOffRate}% drop-off
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Funnel Bar */}
                  <div className="relative h-12 bg-muted/30 rounded-lg overflow-hidden">
                    <div 
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 flex items-center justify-center"
                      style={{ width: `${widthPercentage}%` }}
                    >
                      {widthPercentage > 10 && (
                        <span className="text-xs font-semibold text-white font-caption">
                          {step?.users?.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Drop-off Arrow */}
                  {!isLastStep && parseFloat(step?.dropOffRate) > 0 && (
                    <div className="flex items-center justify-center">
                      <div className="flex items-center gap-2 text-red-500">
                        <Icon name="ArrowDown" size={16} />
                        <span className="text-xs font-caption">
                          {step?.dropOffRate}% dropped
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversionFunnelAnalysis;